import React, { useEffect } from 'react';
import { StyleSheet, View } from 'react-native';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { useUnpaidBillStatementsQuery } from 'apollo/queries/autogenerate/hooks-public';
import { PublicBillStatementQuery } from 'apollo/queries/autogenerate/operations-public';
import { BillStatementStatus } from 'apollo/queries/autogenerate/schemas-public';
import Mixpanel from 'shared/vendor/Mixpanel';
import { ACTION, SCREEN } from 'web/modules/settlement/constants/mixpanel';
import routes from 'web/navigation/routes';
import {
  Error,
  NavigationBar,
} from 'web/screens/Unauthenticated/BillSettlementScreen';
import BillStatementCard from 'web/screens/Unauthenticated/BillsScreen/components/BillStatementCard';
import PendingBillCard from 'web/screens/Unauthenticated/BillsScreen/components/PendingBillCard';

const PAGE_VIEW_UNAUTHENTICATED_BILL = `${SCREEN.UNAUTHENTICATED_BILL} - ${ACTION.PAGE_VIEW}`;

export default function BillsScreen() {
  const navigate = useNavigate();
  const { paidyCode } = useParams();
  const location = useLocation();
  const sourceParam =
    new URLSearchParams(location.search).get('s') === '1' ? 'sms' : 'direct';

  const {
    data: unpaidBillStatements,
    error: unpaidBillStatementsError,
  } = useUnpaidBillStatementsQuery({
    fetchPolicy: 'network-only',
    variables: {
      paidyCode: paidyCode?.trim() || '',
    },
    context: {
      uri: process.env.CONSUMER_API_HOST_GRAPHQL_PUBLIC,
    },
  });

  const sortedBills = unpaidBillStatements
    ? {
        ...unpaidBillStatements,
        unpaidBillStatements: {
          ...unpaidBillStatements.unpaidBillStatements,
          billStatements: unpaidBillStatements.unpaidBillStatements.billStatements
            .map((bill) => ({ ...bill }))
            .sort((bill, nextBill) => {
              const billDate = new Date(bill.dueAt);
              const nextBillDate = new Date(nextBill.dueAt);

              return billDate > nextBillDate ? 1 : -1;
            }),
        },
      }
    : undefined;

  const theOldestUnpaidBillData = sortedBills
    ? (({
        billStatement: {
          ...sortedBills.unpaidBillStatements.billStatements[0],
        },
      } as unknown) as PublicBillStatementQuery)
    : undefined;

  useEffect(() => {
    if (theOldestUnpaidBillData) {
      if (
        theOldestUnpaidBillData.billStatement.status !==
        BillStatementStatus.OVERDUE
      ) {
        if (typeof paidyCode === 'string') {
          navigate(
            routes.unauthenticated.bill.replace(':paidyCode', paidyCode)
          );
        }
      }
    }
  }, [unpaidBillStatements]);

  useEffect(() => {
    Mixpanel.trackEvent(PAGE_VIEW_UNAUTHENTICATED_BILL, {
      source: sourceParam,
      page: '1',
    });
  }, [sourceParam]);

  return (
    <View style={style.root}>
      <NavigationBar />
      <View accessibilityRole="main" style={style.main}>
        <View style={style.container}>
          {unpaidBillStatementsError && <Error />}
          {sortedBills?.unpaidBillStatements && theOldestUnpaidBillData && (
            <PendingBillCard
              theOldestUnpaidBill={theOldestUnpaidBillData}
              bills={sortedBills?.unpaidBillStatements.billStatements}
            />
          )}
          {paidyCode &&
            theOldestUnpaidBillData &&
            sortedBills?.unpaidBillStatements.billStatements.map((bill) => {
              return (
                <BillStatementCard
                  key={bill.id}
                  bill={bill}
                  paidyCode={paidyCode}
                  disabled={
                    bill.id !== theOldestUnpaidBillData.billStatement.id
                  }
                />
              );
            })}
        </View>
      </View>
    </View>
  );
}

const style = StyleSheet.create({
  root: {
    backgroundColor: '#EEF0F2',
    flex: 1,
    position: 'relative',
  },
  main: {
    paddingHorizontal: 16,
    paddingVertical: 24,
    alignItems: 'center',
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    overflow: 'scroll',
  },
  container: {
    width: '100%',
    maxWidth: 980,
  },
});
